<template>
  <div class="access-control-inout-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('accessControlInout.page.title')" :subtitle="$t('accessControlInout.page.subtitle')" />

    <el-row :gutter="16">
      <el-col :xs="24" :sm="6" :md="5" :lg="4">
        <div class="ds-panel ds-panel--padded">
          <div class="ds-panel__header">
            <span class="ds-panel__title">{{ $t('accessControlInout.page.typeTitle') }}</span>
          </div>
          <ul class="ds-side-list">
            <li
              v-for="(item, index) in accessControlInoutInfo.openTypeCds"
              :key="index"
              class="ds-side-list__item"
              :class="{ 'is-active': accessControlInoutInfo.conditions.openTypeCd === item.openTypeCd }"
              @click="swatchOpenTypeCd(item)"
            >
              {{ item.openTypeName }}
            </li>
          </ul>
        </div>
      </el-col>
      <el-col :xs="24" :sm="18" :md="19" :lg="20">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select
                v-model="accessControlInoutInfo.conditions.machineId"
                clearable
                class="w-full"
                :placeholder="$t('accessControlInout.search.machine')"
              >
                <el-option
                  v-for="item in accessControlInoutInfo.accessControls"
                  :key="item.machineId"
                  :label="item.machineName"
                  :value="item.machineId"
                />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input
                v-model="accessControlInoutInfo.conditions.name"
                clearable
                class="w-full"
                :placeholder="$t('accessControlInout.search.name')"
                @keyup.enter.native="_queryAccessControlInoutMethod"
              />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input
                v-model="accessControlInoutInfo.conditions.tel"
                clearable
                class="w-full"
                :placeholder="$t('accessControlInout.search.tel')"
                @keyup.enter.native="_queryAccessControlInoutMethod"
              />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select
                v-model="accessControlInoutInfo.conditions.state"
                clearable
                class="w-full"
                :placeholder="$t('accessControlInout.search.state')"
              >
                <el-option :label="$t('accessControlInout.state.all')" value="" />
                <el-option :label="$t('accessControlInout.state.success')" value="C" />
                <el-option :label="$t('accessControlInout.state.fail')" value="F" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker
                v-model="accessControlInoutInfo.conditions.queryStartTime"
                type="datetime"
                value-format="yyyy-MM-dd HH:mm:ss"
                class="w-full"
                :placeholder="$t('accessControlInout.search.startTime')"
              />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker
                v-model="accessControlInoutInfo.conditions.queryEndTime"
                type="datetime"
                value-format="yyyy-MM-dd HH:mm:ss"
                class="w-full"
                :placeholder="$t('accessControlInout.search.endTime')"
              />
            </el-col>
          </el-row>
          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="_queryAccessControlInoutMethod">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="_resetAccessControlInoutMethod">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar
            :total="page.total"
            :density="density"
            :show-column-setting="false"
            :show-refresh="true"
            @update:density="density = $event"
            @refresh="_listAccessControlInouts(page.current, page.size)"
          >
            <template #left>
              <span class="ds-panel__title">{{ $t('accessControlInout.table.title') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table
            :data="accessControlInoutInfo.accessControlInouts"
            :loading="loading"
            :error="hasError"
            :error-message="errorMessage"
            :density="density"
            border
            :empty-description="$t('accessControlInout.page.empty')"
            @retry="_listAccessControlInouts(page.current, page.size)"
          >
            <el-table-column prop="facePath" :label="$t('accessControlInout.table.face')" align="center" width="100">
              <template slot-scope="scope">
                <el-image
                  style="width:48px;height:48px;cursor:pointer;border-radius:4px"
                  :src="scope.row.facePath || '/img/noPhoto.jpg'"
                  :preview-src-list="scope.row.facePath ? [scope.row.facePath] : []"
                  fit="cover"
                />
              </template>
            </el-table-column>
            <el-table-column prop="inoutId" :label="$t('accessControlInout.table.id')" align="center" width="100">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.inoutId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="machineName" :label="$t('accessControlInout.table.machineName')" align="center" min-width="140" />
            <el-table-column prop="machineCode" :label="$t('accessControlInout.table.machineCode')" align="center" width="140">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.machineCode || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="name" :label="$t('accessControlInout.table.userName')" align="center" min-width="110" />
            <el-table-column prop="openTypeCd" :label="$t('accessControlInout.table.openType')" align="center" width="110">
              <template slot-scope="scope">
                {{ scope.row.openTypeCd === '1000' ? $t('accessControlInout.openType.face') : $t('accessControlInout.openType.other') }}
              </template>
            </el-table-column>
            <el-table-column prop="tel" :label="$t('accessControlInout.table.tel')" align="center" width="130">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.tel || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="idCard" :label="$t('accessControlInout.table.idCard')" align="center" width="170">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.idCard || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="similar" :label="$t('accessControlInout.table.similar')" align="center" width="90">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.similar || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="state" :label="$t('accessControlInout.table.state')" align="center" width="110">
              <template slot-scope="scope">
                <ds-status-tag
                  size="sm"
                  :type="scope.row.state === 'C' ? 'success' : 'danger'"
                  :label="scope.row.state === 'C' ? $t('accessControlInout.state.success') : $t('accessControlInout.state.fail')"
                />
              </template>
            </el-table-column>
            <el-table-column prop="createTime" :label="$t('accessControlInout.table.createTime')" align="center" width="170">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar
            :page="page.current"
            :size="page.size"
            :total="page.total"
            :page-sizes="[10, 20, 30, 50]"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { listAccessControlInouts, listAccessControls } from '@/api/machine/accessControlInoutApi'
import { getCommunityId } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'AccessControlInoutList',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      communityId: '',
      accessControlInoutInfo: {
        accessControlInouts: [],
        accessControls: [],
        openTypeCds: [
          { openTypeName: this.$t('accessControlInout.openType.all'), openTypeCd: '' },
          { openTypeName: this.$t('accessControlInout.openType.face'), openTypeCd: '1000' },
          { openTypeName: this.$t('accessControlInout.openType.key'), openTypeCd: '2000' },
          { openTypeName: this.$t('accessControlInout.openType.qrcode'), openTypeCd: '3000' },
          { openTypeName: this.$t('accessControlInout.openType.password'), openTypeCd: '4000' }
        ],
        total: 0,
        records: 1,
        conditions: {
          machineId: '',
          name: '',
          openTypeCd: '',
          tel: '',
          state: '',
          queryStartTime: '',
          queryEndTime: '',
          iotApiCode: 'listAccessControlInoutBmoImpl',
          page: 1,
          row: 10
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.communityId = getCommunityId()
    this._listAccessControls()
    this._listAccessControlInouts(this.page.current, this.page.size)
  },
  methods: {
    async _listAccessControlInouts(page, rows) {
      this.loading = true
      this.hasError = false
      try {
        this.accessControlInoutInfo.conditions.page = page
        this.accessControlInoutInfo.conditions.row = rows
        this.accessControlInoutInfo.conditions.communityId = this.communityId

        const { data, total } = await listAccessControlInouts(this.accessControlInoutInfo.conditions)
        this.accessControlInoutInfo.accessControlInouts = data
        this.page.total = total
      } catch (error) {
        console.error('获取门禁进出记录失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('common.loadError')
      } finally {
        this.loading = false
      }
    },
    async _listAccessControls() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: this.communityId,
          iotApiCode: 'listAccessControlBmoImpl'
        }
        const { data } = await listAccessControls(params)
        this.accessControlInoutInfo.accessControls = data
      } catch (error) {
        console.error('获取门禁设备失败:', error)
      }
    },
    _queryAccessControlInoutMethod() {
      this.page.current = 1
      this._listAccessControlInouts(this.page.current, this.page.size)
    },
    _resetAccessControlInoutMethod() {
      this.accessControlInoutInfo.conditions.machineId = ''
      this.accessControlInoutInfo.conditions.name = ''
      this.accessControlInoutInfo.conditions.tel = ''
      this.accessControlInoutInfo.conditions.state = ''
      this.accessControlInoutInfo.conditions.queryStartTime = ''
      this.accessControlInoutInfo.conditions.queryEndTime = ''
      this._queryAccessControlInoutMethod()
    },
    swatchOpenTypeCd(openType) {
      this.accessControlInoutInfo.conditions.openTypeCd = openType.openTypeCd
      this._queryAccessControlInoutMethod()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this._listAccessControlInouts(this.page.current, this.page.size)
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listAccessControlInouts(this.page.current, this.page.size)
    }
  }
}
</script>

<style lang="scss" scoped>
.access-control-inout-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &--padded {
    padding: var(--ds-spacing-lg);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-md);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-side-list {
  list-style: none;
  padding: 0;
  margin: 0;

  &__item {
    padding: 10px 12px;
    cursor: pointer;
    text-align: center;
    margin-bottom: 6px;
    border-radius: var(--ds-radius-sm);
    border: 1px solid transparent;
    font-size: var(--ds-font-size-md);
    color: var(--ds-gray-700);

    &:hover {
      background-color: var(--ds-gray-100);
    }

    &.is-active {
      background-color: var(--ds-primary);
      border-color: var(--ds-primary);
      color: #fff;
    }
  }
}

.w-full {
  width: 100%;
}
</style>
